import { useLocation } from 'react-router-dom';
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import type { Modules } from '../config/useUiModules';
// Use BFF auth hook to get current user for header display
import { useAuth } from '@empowernow/bff-auth-react';
import './layout.css';
import { shellLoadingHostStyle } from './shellLoadingHost';
// import { HeaderNav } from '@empowernow/ui';  // ← Replaced with CustomHeaderNav
import { CustomHeaderNav } from './CustomHeaderNav';
import { PluginsMenu, usePermittedPluginRoutes, useResolvedMenuStructure, fetchMenuConfig, useHeaderWidgets } from '../../plugins/dynamicNav';
import { useRecentPlugins } from '../../hooks/useRecentPlugins';
import { useFavoritePlugins } from '../../hooks/useFavoritePlugins';
import type { MenuConfig, PluginManifest } from '../../plugins/types';
import Footer from '../../components/Footer';
import { useTheme } from '../../contexts/ThemeContext';
import { usesEmpowerNowHostBar } from '../theme/brandOwnership';
import { useBrandingContext } from '../../contexts/BrandingContext';
import { useQuickAccess } from '../../contexts/QuickAccessContext';
import { isPublicRoute } from '../auth/publicRoutes';
import { BrandLogo } from '../../components/BrandLogo';
import { GuideMeShell, GuideMeHeaderRecallButton } from '../../guide-me';
import { GlobalLauncher, buildLauncherIndex, rankLauncherResults, getTotalResultCount, DEFAULT_LAUNCHER_LIMITS } from '../launcher';
import type { LauncherSearchItem, LauncherResultSections } from '../launcher';
import {
  trackLauncherOpened,
  trackLauncherResultSelected,
  trackLauncherDismissed,
  trackLauncherQueryChanged,
  trackLauncherZeroResults,
  trackLauncherPinAdded,
  trackLauncherPinRemoved,
  trackLauncherRecentSelected,
  trackLauncherRecentRemoved,
  trackLauncherRecentsCleared,
} from '../launcher/launcherTelemetry';
import { useOperatorMode } from '../../hooks/useOperatorMode';
import { OperatorMobileShell } from './OperatorMobileShell';
import { Sparkles, Puzzle, ShieldCheck, LayoutDashboard, Rocket, type LucideIcon } from 'lucide-react';

// ============================================================================
// MAGNUM: Configuration Loading Component
// Pattern 10: Dashboard Loading Excellence - MAXIMUM LEVEL
// "When even your competition can't help but cheer when they see it"
// 
// Features:
// - Branded logo with sweep animation
// - Rotating status messages reflecting actual loading phases
// - Orbital particles around spinner
// - Dramatic entrance with scale + blur
// - Premium ambient glow effects
// ============================================================================

const LOADING_MESSAGES: { text: string; Icon: LucideIcon }[] = [
  { text: 'Initializing experience...', Icon: Sparkles },
  { text: 'Loading plugins...', Icon: Puzzle },
  { text: 'Checking permissions...', Icon: ShieldCheck },
  { text: 'Preparing menu...', Icon: LayoutDashboard },
  { text: 'Almost ready...', Icon: Rocket },
];

function ConfigurationLoader() {
  const [visible, setVisible] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [messageFade, setMessageFade] = useState(true);
  
  // Magnum: Trigger staggered reveal after mount
  useEffect(() => {
    requestAnimationFrame(() => {
      setVisible(true);
    });
  }, []);
  
  // Magnum: Rotate through loading messages
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageFade(false);
      setTimeout(() => {
        setMessageIndex(prev => (prev + 1) % LOADING_MESSAGES.length);
        setMessageFade(true);
      }, 200);
    }, 2500);
    
    return () => clearInterval(interval);
  }, []);
  
  const currentMessage = LOADING_MESSAGES[messageIndex];
  
  return (
    <div data-shell-loading-host style={shellLoadingHostStyle()}>
    <div 
      className="glass-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '56px 40px',
        margin: 0,
        width: '100%',
        maxWidth: '520px',
        gap: '32px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(30px)',
        transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
      role="status"
      aria-live="polite"
      aria-label="Loading application configuration"
      data-testid="experience-config-loader"
    >
      {/* Ambient background glow */}
      <div
        style={{
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: 'radial-gradient(circle at 50% 50%, color-mix(in srgb, var(--main-accent) 8%, transparent), transparent 50%)',
          animation: 'ambientRotate 20s linear infinite',
          pointerEvents: 'none',
        }}
      />
      
      {/* Logo with sweep animation */}
      <div
        style={{
          position: 'relative',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(-10px)',
          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1) 50ms',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            fontSize: '28px',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #0A3D8F, #3B82F6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.02em',
          }}
        >
          EmpowerID
        </span>
        {/* Sweep shine effect */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '50%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
            animation: 'logoSweep 3s ease-in-out infinite',
          }}
        />
      </div>
      
      {/* Enhanced spinner with orbital particles */}
      <div
        style={{
          position: 'relative',
          width: '80px',
          height: '80px',
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1)' : 'scale(0.5)',
          transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 150ms',
        }}
      >
        {/* Outer glow pulse */}
        <div
          style={{
            position: 'absolute',
            inset: '-16px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, color-mix(in srgb, var(--main-accent) 25%, transparent), transparent 70%)',
            animation: 'glowPulse 2s ease-in-out infinite',
          }}
        />
        
        {/* Orbital ring 1 */}
        <div
          style={{
            position: 'absolute',
            inset: '-8px',
            borderRadius: '50%',
            border: '1px solid color-mix(in srgb, var(--main-accent) 20%, transparent)',
            animation: 'orbitSpin 8s linear infinite',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '-3px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: 'var(--main-accent)',
              boxShadow: '0 0 10px var(--main-accent)',
            }}
          />
        </div>
        
        {/* Orbital ring 2 (counter-rotating) */}
        <div
          style={{
            position: 'absolute',
            inset: '-16px',
            borderRadius: '50%',
            border: '1px dashed color-mix(in srgb, var(--main-accent) 15%, transparent)',
            animation: 'orbitSpinReverse 12s linear infinite',
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: '-2px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              backgroundColor: 'color-mix(in srgb, var(--main-accent) 60%, white)',
              boxShadow: '0 0 8px var(--main-accent)',
            }}
          />
        </div>
        
        {/* Main spinner */}
        <div
          style={{
            width: '100%',
            height: '100%',
            border: '4px solid var(--card-border, rgba(255, 255, 255, 0.1))',
            borderTopColor: 'var(--main-accent)',
            borderRightColor: 'color-mix(in srgb, var(--main-accent) 50%, transparent)',
            borderRadius: '50%',
            animation: 'spin 1s cubic-bezier(0.4, 0, 0.2, 1) infinite',
            boxShadow: '0 0 30px color-mix(in srgb, var(--main-accent) 40%, transparent), inset 0 0 20px color-mix(in srgb, var(--main-accent) 10%, transparent)',
          }}
        />
        
        {/* Inner core */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, var(--main-accent), color-mix(in srgb, var(--main-accent) 50%, transparent))',
            animation: 'corePulse 1.5s ease-in-out infinite',
            boxShadow: '0 0 15px var(--main-accent)',
          }}
        />
      </div>
      
      {/* Rotating status message */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(10px)',
          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1) 250ms',
          minHeight: '60px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            opacity: messageFade ? 1 : 0,
            transform: messageFade ? 'translateY(0)' : 'translateY(-5px)',
            transition: 'all 0.2s ease-out',
          }}
        >
          <span
            style={{ color: 'var(--main-accent)', lineHeight: 0, display: 'inline-flex' }}
            aria-hidden="true"
          >
            <currentMessage.Icon size={20} strokeWidth={1.8} />
          </span>
          <span
            style={{
              fontSize: '17px',
              fontWeight: 600,
              color: 'var(--main-text)',
              letterSpacing: '-0.01em',
            }}
          >
            {currentMessage.text}
          </span>
        </div>
        
        {/* Progress dots */}
        <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
          {LOADING_MESSAGES.map((_, i) => (
            <div
              key={i}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: i === messageIndex 
                  ? 'var(--main-accent)' 
                  : 'var(--card-border, rgba(255, 255, 255, 0.2))',
                transform: i === messageIndex ? 'scale(1.2)' : 'scale(1)',
                transition: 'all 0.3s ease-out',
                boxShadow: i === messageIndex ? '0 0 8px var(--main-accent)' : 'none',
              }}
            />
          ))}
        </div>
      </div>
      
      {/* Enhanced skeleton bars */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1) 350ms',
        }}
      >
        {[100, 80, 60].map((width, i) => (
          <div
            key={i}
            style={{
              height: '10px',
              width: `${width}%`,
              borderRadius: '5px',
              background: 'linear-gradient(90deg, var(--card-border) 0%, color-mix(in srgb, var(--main-accent) 20%, var(--card-border)) 50%, var(--card-border) 100%)',
              backgroundSize: '200% 100%',
              animation: `shimmer 2s ease-in-out infinite ${i * 150}ms`,
              opacity: 0.8,
            }}
          />
        ))}
      </div>
    </div>
    </div>
  );
}

// Inject Magnum loading animations
if (typeof document !== 'undefined' && !document.getElementById('magnum-loading-animations')) {
  const style = document.createElement('style');
  style.id = 'magnum-loading-animations';
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes orbitSpin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes orbitSpinReverse {
      0% { transform: rotate(360deg); }
      100% { transform: rotate(0deg); }
    }
    @keyframes glowPulse {
      0%, 100% { opacity: 0.6; transform: scale(1); }
      50% { opacity: 1; transform: scale(1.1); }
    }
    @keyframes corePulse {
      0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.8; }
      50% { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
    }
    @keyframes shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    @keyframes logoSweep {
      0%, 100% { left: -100%; }
      50% { left: 150%; }
    }
    @keyframes ambientRotate {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

type Props = {
  loading?: boolean;
  modules: Modules;
  children: React.ReactNode;
};

export function LayoutShell({ loading, modules, children }: Props) {
  const { user } = useAuth();
  const { theme } = useTheme();
  const { config: branding } = useBrandingContext();
  const location = useLocation();
  const [themeBranding, setThemeBranding] = useState<any>(null);
  const { isOperator } = useOperatorMode();
  
  // ==========================================================================
  // MEGA MENU: Recent plugins tracking
  // ==========================================================================
  const { recent: recentPluginIds, trackUsage } = useRecentPlugins(5);
  
  // ==========================================================================
  // MEGA MENU: Menu configuration
  // ==========================================================================
  const [menuConfig, setMenuConfig] = useState<MenuConfig | null>(null);
  const [menuConfigLoading, setMenuConfigLoading] = useState(true);
  
  // Fetch menu config on mount
  useEffect(() => {
    let mounted = true;
    
    async function loadMenuConfig() {
      try {
        const config = await fetchMenuConfig();
        if (mounted) {
          setMenuConfig(config);
          if (!config?.menu_structure) {
            // Legitimate diagnostic: the mega menu will fall back to grouping
            // plugins by their `group` field when no menu_structure is configured.
            console.warn('[LayoutShell] No menu_structure in menu config — mega menu will use group-field fallback');
          }
        }
      } catch (error) {
        console.warn('[LayoutShell] Failed to load menu config:', error);
      } finally {
        if (mounted) {
          setMenuConfigLoading(false);
        }
      }
    }
    
    loadMenuConfig();
    return () => { mounted = false; };
  }, []);
  
  // Listen for theme branding changes
  useEffect(() => {
    const handleThemeBranding = (e: any) => {
      // e.detail is null when reverting to defaults
      setThemeBranding(e.detail || null);
    };
    
    window.addEventListener('theme-branding-changed', handleThemeBranding);
    
    // Check if theme branding already set
    if ((window as any).__THEME_BRANDING__) {
      setThemeBranding((window as any).__THEME_BRANDING__);
    }
    
    return () => window.removeEventListener('theme-branding-changed', handleThemeBranding);
  }, []);

  // Core nav items are currently handled by the mega menu + plugin routes.
  // Keep this list empty to avoid needless eager PDP calls at login.
  const items = useMemo(() => ([
    // App Center moved to user dropdown - removed from main nav
    // TEMPORARILY HIDDEN - Uncomment to restore
    // modules.workflow && workflowsAuth.authorized ? { key: 'workflows', label: 'Workflows', path: '/workflows' } : null,
    // modules.workflow ? { key: 'catalog', label: 'Catalog', path: '/catalog' } : null,
    // modules.crud && pagesAuth.authorized ? { key: 'pages', label: 'Pages', path: '/pages' } : null,
    // Don't add static observability - let the plugin handle it
  ].filter(Boolean)) as any, [modules]);

  // Plugin routes ARE injected into HeaderNav for unified navigation

  // Logo filter is now handled by BrandLogo's theme-aware variant selection
  // and the CSS variable --header-logo-filter. No DOM manipulation needed.

  // ==========================================================================
  // HEADER WIDGETS: Resolve plugins contributing header-slot widgets
  // ==========================================================================
  const { widgets: headerWidgets } = useHeaderWidgets();

  // Dynamic plugin routes → consumed by the launcher index + mega menu
  const pluginRoutes = usePermittedPluginRoutes();

  // ==========================================================================
  // MEGA MENU: Build plugin manifest array from routes for menu resolution
  // ==========================================================================
  const pluginManifests = useMemo((): PluginManifest[] => {
    if (pluginRoutes.loading) return [];
    
    // Convert routes to plugin manifests for menu resolver
    // Group routes by pluginId to create one manifest per plugin
    const pluginMap = new Map<string, PluginManifest>();
    
    for (const route of pluginRoutes.routes) {
      if (!pluginMap.has(route.pluginId)) {
        pluginMap.set(route.pluginId, {
          id: route.pluginId,
          title: route.label,
          group: route.group,
          icon: route.icon,
          primary_route: route.path,
          version: '1.0.0',
          engine: { experience: '^1.0.0' },
          enabled: true,
        });
      }
    }
    
    return Array.from(pluginMap.values());
  }, [pluginRoutes.routes, pluginRoutes.loading]);
  
  // ==========================================================================
  // MEGA MENU: Resolve menu structure
  // ==========================================================================
  const { groups: megaMenuGroups, loading: megaMenuLoading } = useResolvedMenuStructure(
    pluginManifests,
    menuConfig,
    // `usePermittedPluginRoutes()` already fail-closed filters by PDP.
    // Avoid a second redundant `/access/v1/evaluations` batch call here.
    { skipAuthCheck: true }
  );
  
  // Handle plugin click for tracking
  const handlePluginClick = useCallback((pluginId: string) => {
    trackUsage(pluginId);
  }, [trackUsage]);

  // ==========================================================================
  // GLOBAL LAUNCHER: State, index, keyboard shortcut
  // ==========================================================================
  const { items: quickAccessItems, removeItem: removeQuickAccessItem, clearAll: clearQuickAccessItems, trackAccess: trackQuickAccess } = useQuickAccess();
  const { pinnedIds, togglePin } = useFavoritePlugins();
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [launcherQuery, setLauncherQuery] = useState('');
  const [launcherSelectedIndex, setLauncherSelectedIndex] = useState(-1);
  const launcherTriggerRef = useRef<HTMLButtonElement>(null);

  // Use full authorized manifests (with aliases/keywords/description) for richer search
  const launcherManifests = pluginRoutes.manifests.length > 0 ? pluginRoutes.manifests : pluginManifests;
  const launcherIndex = useMemo(() => {
    if (launcherManifests.length === 0) return [];
    return buildLauncherIndex({
      manifests: launcherManifests,
      menuGroups: megaMenuGroups,
      quickAccessItems,
      pinnedIds,
    });
  }, [launcherManifests, megaMenuGroups, quickAccessItems, pinnedIds]);

  const currentGroup = useMemo(() => {
    const path = location.pathname;
    for (const group of megaMenuGroups) {
      const items = group.type === 'mega' && group.columns
        ? group.columns.flatMap(c => c.items)
        : group.items ?? [];
      if (items.some(i => path === i.path || path.startsWith(i.path + '/'))) {
        return group.group;
      }
    }
    return undefined;
  }, [megaMenuGroups, location.pathname]);

  const launcherSections: LauncherResultSections = useMemo(() => {
    return rankLauncherResults(launcherIndex, launcherQuery, { currentGroup });
  }, [launcherIndex, launcherQuery, currentGroup]);

  const launcherTotalCount = useMemo(() => getTotalResultCount(launcherSections), [launcherSections]);

  const openLauncher = useCallback((source: 'keyboard' | 'header_button' | 'mega_menu' = 'keyboard') => {
    setLauncherOpen(true);
    setLauncherQuery('');
    setLauncherSelectedIndex(-1);
    trackLauncherOpened(source, window.location.pathname);
  }, []);

  const launcherQueryRef = useRef(launcherQuery);
  launcherQueryRef.current = launcherQuery;
  const launcherTotalCountRef = useRef(launcherTotalCount);
  launcherTotalCountRef.current = launcherTotalCount;

  const closeLauncher = useCallback(() => {
    trackLauncherDismissed(launcherQueryRef.current.length, launcherTotalCountRef.current > 0);
    setLauncherOpen(false);
    setLauncherQuery('');
    setLauncherSelectedIndex(-1);
  }, []);

  const handleLauncherSelect = useCallback((item: LauncherSearchItem) => {
    trackUsage(item.id);
    const allItems = [
      ...launcherSections.pinned,
      ...launcherSections.recent,
      ...launcherSections.bestMatch,
      ...launcherSections.otherMatches,
      ...launcherSections.actions,
    ];
    const rank = allItems.findIndex(i => i.id === item.id);
    trackLauncherResultSelected(item.id, rank, launcherQuery.length);
    if (item.lastAccessedAt) {
      trackLauncherRecentSelected(item.id);
    }
    setLauncherOpen(false);
    setLauncherQuery('');
    setLauncherSelectedIndex(-1);
    setTimeout(() => {
      window.location.href = item.route;
    }, 50);
  }, [trackUsage, launcherSections, launcherQuery.length]);

  const handleLauncherPinToggle = useCallback((item: LauncherSearchItem) => {
    togglePin(item.id);
    if (item.pinned) {
      trackLauncherPinRemoved(item.id);
    } else {
      trackLauncherPinAdded(item.id);
    }
  }, [togglePin]);

  const handleLauncherRemoveRecent = useCallback((pluginId: string) => {
    removeQuickAccessItem(pluginId);
    trackLauncherRecentRemoved(pluginId);
  }, [removeQuickAccessItem]);

  const handleLauncherClearRecents = useCallback(() => {
    const count = quickAccessItems.length;
    clearQuickAccessItems();
    trackLauncherRecentsCleared(count);
  }, [clearQuickAccessItems, quickAccessItems.length]);

  const handleLauncherRestoreRecent = useCallback((item: LauncherSearchItem) => {
    trackQuickAccess(item.id, {
      title: item.title,
      path: item.route,
      icon: item.icon,
      group: item.group,
    });
  }, [trackQuickAccess]);

  const queryTelemetryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleLauncherQueryChange = useCallback((q: string) => {
    setLauncherQuery(q);
    setLauncherSelectedIndex(q.trim() ? 0 : -1);
    // Debounce telemetry to avoid flooding on fast typing
    if (queryTelemetryTimer.current) clearTimeout(queryTelemetryTimer.current);
    if (q.trim().length > 0) {
      queryTelemetryTimer.current = setTimeout(() => {
        trackLauncherQueryChanged(q.trim().length);
      }, 300);
    }
  }, []);

  // Track zero-result state (debounced by React render cycle)
  useEffect(() => {
    if (launcherOpen && launcherQuery.trim().length >= DEFAULT_LAUNCHER_LIMITS.zeroResultMinQueryLength && launcherTotalCount === 0) {
      trackLauncherZeroResults(launcherQuery.trim().length);
    }
  }, [launcherOpen, launcherQuery, launcherTotalCount]);

  // Global Ctrl+K / Cmd+K shortcut with guard
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        if (isPublicRoute()) return;

        const target = e.target as HTMLElement;
        const tagName = target?.tagName?.toLowerCase();
        const isEditable = tagName === 'input' || tagName === 'textarea' || tagName === 'select'
          || target?.isContentEditable || target?.getAttribute('role') === 'textbox';

        if (isEditable) return;

        e.preventDefault();
        if (launcherOpen) {
          closeLauncher();
        } else {
          openLauncher();
        }
      }
    };

    // Keyboard nav while launcher is open (arrows only; Enter is handled inside GlobalLauncher for hover-aware selection)
    const handleLauncherKeys = (e: KeyboardEvent) => {
      if (!launcherOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setLauncherSelectedIndex(prev => Math.min(prev + 1, launcherTotalCount - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setLauncherSelectedIndex(prev => Math.max(prev - 1, -1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keydown', handleLauncherKeys);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keydown', handleLauncherKeys);
    };
  }, [launcherOpen, launcherTotalCount, openLauncher, closeLauncher]);

  // Close launcher on route change — ref avoids re-subscribing on every open/close toggle
  const launcherOpenRef = useRef(launcherOpen);
  launcherOpenRef.current = launcherOpen;

  useEffect(() => {
    if (launcherOpenRef.current) closeLauncher();
  }, [location.pathname, closeLauncher]);


  // Plugin navigation is served exclusively by the mega menu (`megaMenuGroups`,
  // resolved in `useResolvedMenuStructure`, which itself falls back to legacy
  // group-by-field when no `menu_structure` is configured). The previous
  // per-render `dynamicItems` grouping here was a redundant second legacy
  // implementation of that same fallback; it has been deleted (cutover, not
  // fenced) so there is a single source of truth for plugin nav. Only the
  // static base `items` are passed through to the header now.

  if (isOperator && !isPublicRoute()) {
    return (
      <OperatorMobileShell loading={loading}>
        {children}
      </OperatorMobileShell>
    );
  }

  if (isPublicRoute()) {
    return (
      <div className="min-h-screen emp-app" data-testid="experience-shell-public" style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
        <div className="header-wrapper" data-testid="experience-header">
          <nav
            className="custom-header-nav"
            aria-label="Primary"
            data-testid="experience-nav"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 20px',
              height: 64,
              background: 'var(--header-bg)',
              WebkitBackdropFilter: 'var(--header-backdrop-filter, saturate(1.1) blur(6px))',
              backdropFilter: 'var(--header-backdrop-filter, saturate(1.1) blur(6px))',
              borderBottom: 'var(--header-border-bottom, 1px solid var(--border-color))',
              boxShadow: 'var(--header-shadow, none)',
              position: 'sticky',
              top: 0,
              zIndex: 50,
            }}
          >
            <a
              href="/"
              style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', gap: 10 }}
              aria-label="EmpowerID Home"
            >
              <BrandLogo height={28} alt="" style={{ filter: 'var(--header-logo-filter, none)' } as React.CSSProperties} />
              <span style={{
                fontSize: 17,
                fontWeight: 400,
                color: 'var(--header-text, rgba(255,255,255,0.7))',
                opacity: 0.7,
                letterSpacing: '0.01em',
              }}>
                {themeBranding?.appName || branding?.appName || 'Experience'}
              </span>
            </a>
            <a
              href="/auth/login"
              style={{
                padding: '8px 20px',
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--header-text, rgba(255,255,255,0.9))',
                textDecoration: 'none',
                borderRadius: 6,
                border: '1px solid rgba(255,255,255,0.2)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
              }}
            >
              Sign in
            </a>
          </nav>
        </div>
        <main id="main" role="main" data-testid="experience-main" style={{ flex: 1 }}>
          {loading ? <ConfigurationLoader /> : children}
        </main>
      </div>
    );
  }

  return (
    <GuideMeShell>
    <div className="min-h-screen emp-app" data-testid="experience-shell" style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
      {/* Skip link for keyboard users to bypass header/navigation */}
      <a href="#main" className="emp-skip-link" data-testid="experience-skip-to-main">Skip to main content</a>

      <div
        className="header-wrapper"
        data-testid="experience-header"
        /* Brand-ownership host bar: platform-owned themes wear the EmpowerID navy
           bar with white chrome. Overriding the header CSS vars here cascades to the
           wrapper, the nav, and every descendant that reads --header-* (single lever,
           no per-theme edits). Customer white-label themes fall through to their own
           --header-* tokens. */
        style={
          usesEmpowerNowHostBar(theme)
            ? ({
                ['--header-bg']: 'var(--brand-host-bg, #0a2a52)',
                ['--header-text']: '#ffffff',
                ['--header-text-hover']: '#ffffff',
                ['--border-color']: 'rgba(255,255,255,0.14)',
                ['--header-border-bottom']: '1px solid rgba(255,255,255,0.12)',
                // Force the EmpowerID Identity Fabric wordmark fully white on the navy bar — the
                // brand dark-bg asset keeps a violet "Now" (~3.3:1 on #0a2a52); the
                // spec calls for a white wordmark, so flatten it to white.
                ['--header-logo-filter']: 'brightness(0) invert(1)',
                // Recolor the launcher pill + user-menu chrome to a translucent inset so
                // the (now white) label text reads on navy — the light theme's default
                // pill bg (#f8fafc) would wash white text out. These are the same
                // white-alpha values the dark themes use for the pill.
                ['--launcher-pill-bg']: 'rgba(255,255,255,0.08)',
                ['--launcher-pill-border']: 'rgba(255,255,255,0.16)',
                ['--launcher-pill-hover-bg']: 'rgba(255,255,255,0.14)',
                ['--launcher-pill-hover-border']: 'rgba(255,255,255,0.30)',
                ['--launcher-kbd-bg']: 'rgba(255,255,255,0.12)',
                ['--launcher-kbd-border']: 'rgba(255,255,255,0.18)',
                ['--user-menu-border']: 'rgba(255,255,255,0.18)',
                ['--user-menu-trigger-hover-bg']: 'rgba(255,255,255,0.10)',
                ['--user-menu-trigger-hover-border']: 'rgba(255,255,255,0.30)',
              } as React.CSSProperties)
            : undefined
        }
      >
        <CustomHeaderNav 
          items={items} 
          showThemeToggle={false} 
          showSearch={false}
          brand={{
            logo: themeBranding?.logos?.primary || branding?.logos?.primary || '/en-logo-light.png',
            title: themeBranding?.appName || branding?.appName || 'Experience',
            href: '/',
          }}
          userName={
            (user as any)?.name ||
            (user as any)?.preferred_username ||
            ((user as any)?.given_name && (user as any)?.family_name
              ? `${(user as any)?.given_name} ${(user as any)?.family_name}`
              : undefined) ||
            (user as any)?.email ||
            (user as any)?.sub ||
            'User'
          }
          onLogout={() => {
            // Submit a real POST to BFF logout to avoid prefetch or GET caching issues
            try {
              const form = document.createElement('form');
              form.method = 'POST';
              form.action = '/auth/logout?everywhere=true';
              const hidden = document.createElement('input');
              hidden.type = 'hidden';
              hidden.name = 'everywhere';
              hidden.value = 'true';
              form.appendChild(hidden);
              document.body.appendChild(form);
              form.submit();
            } catch {
              // Fallback – navigate
              const returnTo = encodeURIComponent('/');
              window.location.assign(`/auth/logout?everywhere=true&return_to=${returnTo}`);
            }
          }}
          // Mega Menu Props
          megaMenuGroups={megaMenuGroups.length > 0 ? megaMenuGroups : undefined}
          recentPluginIds={recentPluginIds}
          onPluginClick={handlePluginClick}
          megaMenuLoading={megaMenuLoading || menuConfigLoading || pluginRoutes.loading}
          // Global Launcher
          onLauncherOpen={() => openLauncher('header_button')}
          launcherTriggerRef={launcherTriggerRef}
          globalSearchItems={launcherIndex}
          headerSlots={
            <>
              <GuideMeHeaderRecallButton />
              {headerWidgets.length > 0 && (
                <div className="header-widgets-slot" data-testid="experience-header-widgets" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {headerWidgets.map((hw) => (
                    <React.Suspense key={hw.pluginId} fallback={null}>
                      <hw.component />
                    </React.Suspense>
                  ))}
                </div>
              )}
            </>
          }
        />
      </div>
      
      <main id="main" className="emp-main" role="main" data-testid="experience-main" style={{ flex: 1 }}>
        {loading ? <ConfigurationLoader /> : children}
      </main>
      
      <Footer />
      

      {/* Global Experience Launcher */}
      <GlobalLauncher
        open={launcherOpen}
        sections={launcherSections}
        query={launcherQuery}
        selectedIndex={launcherSelectedIndex}
        totalResultCount={launcherTotalCount}
        onQueryChange={handleLauncherQueryChange}
        onSelect={handleLauncherSelect}
        onPinToggle={handleLauncherPinToggle}
        onRemoveRecent={handleLauncherRemoveRecent}
        onClearRecents={handleLauncherClearRecents}
        onRestoreRecent={handleLauncherRestoreRecent}
        onClose={closeLauncher}
        triggerRef={launcherTriggerRef}
      />
    </div>
    </GuideMeShell>
  );
}


